import React, { useState, useEffect, useRef } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const QrCodeGeneratorPage = () => {
  const [text, setText] = useState('');
  const [size, setSize] = useState(256);
  const [errorLevel, setErrorLevel] = useState('M');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const canvasRef = useRef(null);

  // QR Code generation using qr-code-generator library (we'll implement a simple version)
  const generateQRCode = async () => {
    if (!text.trim()) {
      setQrCodeUrl('');
      return;
    }

    setIsGenerating(true);
    
    try {
      // Using QR Server API for QR code generation
      const params = new URLSearchParams({
        data: text,
        size: `${size}x${size}`,
        bgcolor: backgroundColor.replace('#', ''),
        color: foregroundColor.replace('#', ''),
        ecc: errorLevel
      });
      
      const url = `https://api.qrserver.com/v1/create-qr-code/?${params}`;
      setQrCodeUrl(url);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate QR code when inputs change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      generateQRCode();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [text, size, errorLevel, backgroundColor, foregroundColor]);

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `qrcode-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = async () => {
    if (!qrCodeUrl) return;

    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      alert('QR code copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy QR code:', error);
      alert('Failed to copy QR code. Please try downloading instead.');
    }
  };

  const presetTexts = [
    { label: 'Website URL', value: 'https://yourjsontools.com' },
    { label: 'Email', value: 'mailto:test@yourjsontools.com' },
    { label: 'Phone', value: 'tel:+1234567890' },
    { label: 'WiFi', value: 'WIFI:T:WPA;S:NetworkName;P:Password;;' },
    { label: 'SMS', value: 'sms:+1234567890?body=Hello' },
    { label: 'Location', value: 'geo:37.7749,-122.4194' }
  ];

  const errorLevels = [
    { value: 'L', label: 'Low (~7%)', description: 'Suitable for clean environments' },
    { value: 'M', label: 'Medium (~15%)', description: 'Balanced option for most uses' },
    { value: 'Q', label: 'Quartile (~25%)', description: 'Good for slightly damaged codes' },
    { value: 'H', label: 'High (~30%)', description: 'Best for harsh environments' }
  ];

  return (
    <div>
      <SEO 
        title="QR Code Generator - Create Custom QR Codes Online" 
        description="Free online QR code generator. Create QR codes for URLs, text, email, phone numbers, WiFi, and more. Customize colors and size. Download instantly."
        keywords="qr code generator, qr code maker, create qr code, custom qr code, qr code online, free qr generator"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>QR Code Generator</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Create custom QR codes for URLs, text, contact information, and more. Customize colors and download instantly.
        </p>
        
        <AdUnit />

        <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px' }}>
          {/* Input Section */}
          <div className="input-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>QR Code Content</h2>
            
            {/* Text Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Text or URL:
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter text, URL, email, phone number, or any content..."
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                {text.length} characters
              </div>
            </div>

            {/* Preset Templates */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Quick Templates:
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {presetTexts.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => setText(preset.value)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '16px',
                      backgroundColor: 'white',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f0f0f0';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Customization Options */}
            <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', marginTop: '25px' }}>Customization</h3>
            
            {/* Size */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Size: {size}x{size} pixels
              </label>
              <input
                type="range"
                min="128"
                max="512"
                step="32"
                value={size}
                onChange={(e) => setSize(parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                <span>128px</span>
                <span>512px</span>
              </div>
            </div>

            {/* Error Correction Level */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Error Correction Level:
              </label>
              <select
                value={errorLevel}
                onChange={(e) => setErrorLevel(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                {errorLevels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label} - {level.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Colors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Foreground Color:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="color"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                    style={{ width: '40px', height: '32px', border: 'none', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Background Color:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    style={{ width: '40px', height: '32px', border: 'none', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="preview-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Preview & Download</h2>
            
            <div 
              className="qr-preview"
              style={{
                border: '2px dashed #ddd',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#fafafa',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isGenerating ? (
                <div style={{ color: '#666' }}>
                  <div style={{ fontSize: '18px', marginBottom: '10px' }}>⏳</div>
                  <div>Generating QR Code...</div>
                </div>
              ) : qrCodeUrl && text.trim() ? (
                <div>
                  <img
                    src={qrCodeUrl}
                    alt="Generated QR Code"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginBottom: '15px'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '15px' }}>
                    {size}x{size} pixels • Error Level: {errorLevel}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button
                      onClick={downloadQRCode}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={copyToClipboard}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#666', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}>📱</div>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>Enter text to generate QR code</div>
                  <div style={{ fontSize: '14px' }}>Your QR code will appear here</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About QR Codes</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>What are QR Codes?</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                QR (Quick Response) codes are two-dimensional barcodes that can store various types of information 
                including URLs, text, contact information, WiFi credentials, and more. They can be scanned by 
                smartphones and other devices with cameras.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Error Correction</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                QR codes include error correction that allows them to be read even when partially damaged or obscured. 
                Higher error correction levels provide more redundancy but result in denser codes.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Use Cases</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Common uses include sharing website URLs, contact information, WiFi passwords, product information, 
                event details, payment information, and social media profiles.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 20px;
          }
          
          .qr-preview {
            min-height: 250px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default QrCodeGeneratorPage; 